import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Rating,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Container
} from '@mui/material';
import { Star, StarBorder } from '@mui/icons-material';
import { useQuery, useMutation } from 'react-query';
import api from '../config/api';

const Feedback: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [rating, setRating] = useState<number>(0);
  const [comment, setComment] = useState<string>('');

  // Fetch feedback details
  const { data: feedbackData, isLoading, error } = useQuery(
    ['feedback', token],
    async () => {
      const response = await api.get(`/api/feedback/${token}`);
      return response.data;
    },
    {
      enabled: !!token,
      retry: false
    }
  );

  // Submit feedback mutation
  const submitFeedbackMutation = useMutation(
    async (data: { rating: number; comment: string }) => {
      const response = await api.post(`/api/feedback/${token}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        navigate('/feedback/thank-you');
      }
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (rating < 1 || rating > 5) {
      return;
    }
    submitFeedbackMutation.mutate({ rating, comment });
  };

  if (isLoading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !feedbackData?.feedback) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error">
          Feedback not found or has expired.
        </Alert>
      </Container>
    );
  }

  const feedback = feedbackData.feedback;

  if (feedback.is_submitted) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="info">
          Thank you! Your feedback has already been submitted.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            How was your support experience?
          </Typography>
          
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
            Your feedback helps us improve our service quality.
          </Typography>

          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Rating
                name="rating"
                value={rating}
                onChange={(event, newValue) => {
                  setRating(newValue || 0);
                }}
                size="large"
                icon={<Star fontSize="inherit" />}
                emptyIcon={<StarBorder fontSize="inherit" />}
                max={5}
                sx={{
                  '& .MuiRating-icon': {
                    fontSize: '3rem'
                  }
                }}
              />
            </Box>

            {rating > 0 && (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                {rating === 1 && 'Poor'}
                {rating === 2 && 'Fair'}
                {rating === 3 && 'Good'}
                {rating === 4 && 'Very Good'}
                {rating === 5 && 'Excellent'}
              </Typography>
            )}

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Additional Comments (Optional)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              sx={{ mb: 3 }}
              placeholder="Tell us more about your experience..."
            />

            {submitFeedbackMutation.isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to submit feedback. Please try again.
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                onClick={handleSubmit}
                variant="contained"
                size="large"
                disabled={rating < 1 || submitFeedbackMutation.isLoading}
                sx={{ minWidth: 120 }}
              >
                {submitFeedbackMutation.isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  'Submit Feedback'
                )}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Feedback;
